import styled from "styled-components";

const surfaceFieldStyles = `
  width: 100%;
  border: 1px solid var(--brand-border);
  border-radius: 1rem;
  background: rgba(10, 13, 18, 0.72);
  color: var(--brand-ink);
  padding: 0.95rem 1rem;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;

  &:focus {
    border-color: var(--brand-highlight);
    box-shadow: var(--brand-input-focus-ring);
    outline: none;
  }

  &[aria-invalid="true"] {
    border-color: var(--brand-error-border);
    box-shadow: 0 0 0 3px var(--brand-error-ring);
  }
`;

const selectMenuStyles = `
  color-scheme: dark;

  option,
  optgroup {
    background: #151b23;
    color: var(--brand-ink);
  }

  option:disabled {
    color: var(--brand-muted);
  }
`;

const actionButtonStyles = `
  align-items: center;
  border-radius: 1rem;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.85rem 1rem;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

export const SectionStatsGrid = styled.div<{ $tight?: boolean }>`
  display: grid;
  gap: 1.25rem;
  margin-bottom: ${({ $tight }) => ($tight ? "0.75rem" : "2rem")};

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const SectionContentGrid = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  }
`;

export const SectionFormGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

export const SectionStack = styled.div`
  display: grid;
  gap: 1rem;
`;

export const TwoColumnFieldGrid = styled.div`
  display: grid;
  gap: 0.75rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const FormField = styled.label`
  display: grid;
  gap: 0.5rem;
`;

export const FieldLabel = styled.span`
  color: var(--brand-muted);
  font-size: 0.8125rem;
`;

export const FieldErrorText = styled.p`
  color: var(--brand-error-text);
  font-size: 0.75rem;
`;

export const FormSelect = styled.select`
  ${surfaceFieldStyles}
  ${selectMenuStyles}
`;

export const FormInput = styled.input`
  ${surfaceFieldStyles}
`;

export const FormTextArea = styled.textarea`
  ${surfaceFieldStyles}
  min-height: 7rem;
  resize: vertical;
`;

export const PrimaryActionButton = styled.button`
  ${actionButtonStyles}
  background: var(--brand-highlight);
  color: #1b1306;

  &:hover:not(:disabled) {
    background: var(--brand-accent-deep);
    color: white;
  }
`;

export const SecondaryActionButton = styled.button`
  ${actionButtonStyles}
  background: rgba(255, 255, 255, 0.04);
  border-color: var(--brand-border);
  color: var(--brand-ink);

  &:hover:not(:disabled) {
    border-color: var(--brand-highlight);
    background: rgba(240, 191, 86, 0.08);
  }
`;

export const IconActionButton = styled.button`
  ${actionButtonStyles}
  background: rgba(220, 38, 38, 0.08);
  border-color: rgba(254, 202, 202, 0.2);
  color: #fecaca;
  min-height: 2.5rem;
  min-width: 2.5rem;
  padding: 0;

  &:hover:not(:disabled) {
    background: rgba(220, 38, 38, 0.16);
  }
`;

export const CheckoutOtpCheckboxRow = styled.label`
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--brand-border);
  border-radius: 1rem;
  color: var(--brand-ink);
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
`;

export const ActionDivider = styled.div`
  border-top: 1px solid var(--brand-border);
  padding-top: 1rem;
`;

export const MutedPanel = styled.div`
  background: var(--brand-panel-muted);
  border: 1px solid var(--brand-border);
  border-radius: 1.5rem;
  padding: 1rem;
`;

export const PanelHeaderRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

export const PanelTitleText = styled.p`
  color: var(--brand-ink);
  font-size: 0.875rem;
  font-weight: 600;
`;

export const PanelDescriptionText = styled.p`
  color: var(--brand-muted);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

export const PanelAccentText = styled.p`
  color: var(--brand-highlight);
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

export const PanelBodyStack = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
`;

export const CheckoutOtpCheckboxText = styled.span`
  color: var(--brand-ink);
  font-size: 0.8125rem;
`;

export const HeroSurfacePanel = styled.div`
  background: var(--brand-hero-surface);
  border: 1px solid var(--brand-border);
  border-radius: 1.75rem;
  overflow: hidden;
  padding: 1.25rem;
`;

export const MetricGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const DashedNotice = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed var(--brand-border);
  border-radius: 1rem;
  color: var(--brand-muted);
  font-size: 0.875rem;
  line-height: 1.7;
  padding: 1rem;
`;

export const ToolbarSelect = styled.select`
  ${surfaceFieldStyles}
  ${selectMenuStyles}
  min-width: 0;

  @media (min-width: 640px) {
    min-width: 15rem;
    width: auto;
  }
`;
